<template>
  <div class="container-fluid p-0">
    <!-- Combined Sales Summary and Strategy Selection Panel -->
    <div class="row g-3 mb-3">
      <div class="col-12">
        <div class="card">
          <div class="card-header bg-light py-2">
            <h6 class="card-title mb-0 fw-semibold">
              {{ t("dashboard.portfolio.sellGoOrderSummary") }}
            </h6>
          </div>
          <div class="card-body">
            <div class="row">
              <!-- Summary Info Section -->
              <div class="col-lg-12">
                <div
                  v-if="selectedPortfolioItems.length === 0"
                  class="text-muted mb-3"
                >
                  <i class="fas fa-info-circle me-2 text-muted"></i>
                  {{ t("dashboard.portfolio.noCertificatesSelectedSell") }}
                </div>
                <div
                  v-else
                  class="d-flex align-items-center flex-wrap gap-4 mb-3"
                >
                  <!-- Total MWh -->
                  <div class="d-flex align-items-center">
                    <i class="fas fa-bolt text-warning me-2"></i>
                    <div>
                      <div class="fw-semibold text-dark">
                        {{ totalMwhToSell }} MWh
                      </div>
                      <div class="small text-muted">
                        {{ t("dashboard.portfolio.totalMwhToSell") }}
                      </div>
                    </div>
                  </div>

                  <!-- Technologies -->
                  <div
                    v-if="selectedTechnologies"
                    class="d-flex align-items-center"
                  >
                    <i class="fas fa-cog text-primary me-2"></i>
                    <div>
                      <div class="fw-semibold text-dark">
                        {{ selectedTechnologies }}
                      </div>
                      <div class="small text-muted">
                        {{ t("dashboard.portfolio.technologies") }}
                      </div>
                    </div>
                  </div>

                  <!-- Production Months -->
                  <div
                    v-if="selectedProductionMonths"
                    class="d-flex align-items-center"
                  >
                    <i class="fas fa-calendar text-info me-2"></i>
                    <div>
                      <div class="fw-semibold text-dark">
                        {{ selectedProductionMonths }}
                      </div>
                      <div class="small text-muted">
                        {{ t("dashboard.portfolio.production_months") }}
                      </div>
                    </div>
                  </div>

                  <!-- Strategy Info -->
                  <div
                    v-if="selectedPortfolioItems.length > 0"
                    class="d-flex align-items-center"
                  >
                    <i class="fas fa-arrow-right text-success me-2"></i>
                    <div>
                      <div class="fw-semibold text-dark">
                        {{ getStrategyDisplayText() }}
                      </div>
                      <div class="small text-muted">
                        {{
                          selectedStrategy.type === "minimum"
                            ? t(
                                "dashboard.portfolio.spot_sales_min_price_description"
                              )
                            : t(
                                "dashboard.portfolio.spotSalesPortfolioDescription"
                              )
                        }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Strategy Selection Section -->
                <div v-if="selectedPortfolioItems.length > 0" class="mb-3">
                  <SpotStrategyPicker
                    :initialStrategy="initialStrategy"
                    @update:strategy="handleStrategyChanged"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Old Certificates Warning -->
    <div
      v-if="hasOldCertificates"
      class="alert alert-warning mb-3"
      role="alert"
    >
      <div class="d-flex align-items-start">
        <i class="fas fa-exclamation-triangle me-2 mt-1"></i>
        <div>
          <strong>{{
            t("dashboard.portfolio.oldCertificatesWarningTitle")
          }}</strong>
          <div class="mt-1">
            {{
              t("dashboard.portfolio.oldCertificatesWarningMessage", {
                count: oldCertificatesCount,
                duration: props.company.id
                  ? productionStore.getDurationMonthsForCompany(
                      props.company.id
                    )
                  : 6,
              })
            }}
          </div>
        </div>
      </div>
    </div>

    <!-- Portfolio Items Table - Full Width -->
    <div class="row g-3">
      <div class="col-12">
        <h5 class="mb-3">
          {{ t("dashboard.portfolio.selected_certificates") }}
        </h5>
        <PortfolioItemsTable
          :selection="selection"
          :enable-count-editing="true"
          :default-visible-columns="TRANSACTION_COLUMNS"
          storage-context="sell"
        >
          <template #footer-action>
            <button
              type="button"
              class="btn btn-success"
              @click="handleConfirmSellOrder"
              :disabled="
                !isOrderComplete ||
                isConfirmingOrder ||
                !selectedStrategy.isValid ||
                hasOldCertificates
              "
            >
              <span
                v-if="isConfirmingOrder"
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              {{ t("common.actions.confirm_sell") }}
            </button>
          </template>
        </PortfolioItemsTable>
      </div>
    </div>

    <!-- Confirmation Modal -->
    <SellGoReviewModal
      ref="reviewModalRef"
      :totalMwhToSell="totalMwhToSell"
      :selectedTechnologies="selectedTechnologies ?? ''"
      :selectedProductionMonths="selectedProductionMonths ?? ''"
      :summarySupport="summarySupport ?? ''"
      :selectedStrategy="strategyWithResolvedDuration"
      :isLoading="isConfirmingOrder"
      @confirm="handleModalConfirm"
    />
  </div>
</template>

<script setup lang="ts">
import { useCompanySpotStrategy } from "@/composables/useCompanySpotStrategy";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import SpotStrategyPicker from "@/dashboard/common/SpotStrategyPicker.vue";
import PortfolioItemsTable from "@/dashboard/inventory_management/common/PortfolioItemsTable.vue";
import { usePortfolioSelection } from "@/dashboard/inventory_management/common/usePortfolioSelection";
import { TRANSACTION_COLUMNS } from "@/dashboard/inventory_management/common/portfolioTableColumns";
import { useSelectedPortfolioSummary } from "@/dashboard/inventory_management/common/useSelectedPortfolioSummary";
import SellGoReviewModal from "@/dashboard/inventory_management/sell/SellGoReviewModal.vue";
import { useProductionStrategyStore } from "@/dashboard/production_strategy/useProductionStrategyStore";
import type { Company } from "@/types";
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  useTemplateRef,
} from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  company: Company;
}>();

const { t } = useI18n();
const selection = usePortfolioSelection({ syncToUrl: true });
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();
const productionStore = useProductionStrategyStore();
const initialStrategy = useCompanySpotStrategy();
const {
  selectedPortfolioItems,
  totalSelectedMwh,
  selectedTechnologies,
  selectedProductionMonths,
  summarySupport,
} = useSelectedPortfolioSummary(selection);

const isConfirmingOrder = ref(false);
const reviewModalRef =
  useTemplateRef<InstanceType<typeof SellGoReviewModal>>("reviewModalRef");

// Strategy state
const selectedStrategy = ref<{
  type: "market" | "minimum";
  minimumPrice?: number;
  durationMonths?: number;
  isValid: boolean;
}>({
  type: "market",
  minimumPrice: undefined,
  durationMonths: undefined,
  isValid: true,
});

const totalMwhToSell = computed(() => {
  return totalSelectedMwh.value;
});

// Check if selected certificates are older than the configured duration (based on SPOT_MIN_PRICE portfolio)
const oldCertificatesCount = computed(() => {
  if (selectedPortfolioItems.value.length === 0) return 0;
  if (selectedStrategy.value.type !== "minimum") return 0;

  const durationMonths =
    selectedStrategy.value.durationMonths ??
    (props.company.id
      ? productionStore.getDurationMonthsForCompany(props.company.id)
      : 6);

  const cutoffDate = new Date();
  cutoffDate.setMonth(cutoffDate.getMonth() - durationMonths);

  return selectedPortfolioItems.value.reduce((total, item) => {
    if (!item.production_month) return total;

    const productionDate = new Date(item.production_month + "-01");

    if (productionDate < cutoffDate) {
      return total + (selection.getSelectedCount(item.id) ?? 0);
    }
    return total;
  }, 0);
});

const hasOldCertificates = computed(() => {
  return oldCertificatesCount.value > 0;
});

const isOrderComplete = computed(() => {
  return (
    selectedPortfolioItems.value.length > 0 &&
    totalMwhToSell.value > 0 &&
    !isConfirmingOrder.value &&
    !hasOldCertificates.value
  );
});

const handleStrategyChanged = (strategy: {
  type: "market" | "minimum";
  minimumPrice?: number;
  durationMonths?: number;
  isValid: boolean;
}) => {
  nextTick(() => {
    selectedStrategy.value = {
      type: strategy.type,
      minimumPrice:
        strategy.type === "minimum"
          ? Number(strategy.minimumPrice) || undefined
          : undefined,
      durationMonths:
        strategy.type === "minimum"
          ? Number(strategy.durationMonths) || undefined
          : undefined,
      isValid: strategy.isValid,
    };
  });
};

const getStrategyDisplayText = () => {
  if (selectedStrategy.value.type === "market") {
    return t("dashboard.portfolio.footer.strategy_auto_high");
  }

  const price = selectedStrategy.value.minimumPrice;
  const duration = selectedStrategy.value.durationMonths ?? 6;
  if (price !== undefined && typeof price === "number" && !isNaN(price)) {
    return t("dashboard.portfolio.footer.strategy_min_price_detailed", {
      price: price.toFixed(2),
      duration,
    });
  }

  return t("dashboard.portfolio.footer.strategy_min_price");
};

const strategyWithResolvedDuration = computed(() => {
  return {
    ...selectedStrategy.value,
    durationMonths:
      selectedStrategy.value.durationMonths ??
      (props.company.id
        ? productionStore.getDurationMonthsForCompany(props.company.id)
        : 6),
  };
});

const handleConfirmSellOrder = async () => {
  if (!isOrderComplete.value || isConfirmingOrder.value) return;
  reviewModalRef.value?.open();
};

const handleModalConfirm = async () => {
  isConfirmingOrder.value = true;

  try {
    const orderPayload = {
      selected_portfolio_items: selection.selectedItemsForApi,
      strategy: {
        type: selectedStrategy.value.type,
        minimumPrice: selectedStrategy.value.minimumPrice,
      },
    };

    const currentCompanyId = props.company.id;
    if (!currentCompanyId) {
      globalErrorHandler(new Error(t("errors.missingCompanyId")));
      isConfirmingOrder.value = false;
      return;
    }

    await goSolidApi.post(
      `/api/company/${currentCompanyId}/portfolio/sell`,
      orderPayload
    );

    globalSuccessHandler(
      t("dashboard.portfolio.sellGoSuccessTitle"),
      t("dashboard.portfolio.sellGoSuccessMessage")
    );

    window.location.reload();
  } catch (error: any) {
    globalErrorHandler(error);
  } finally {
    isConfirmingOrder.value = false;
  }
};

onMounted(async () => {
  try {
    if (props.company.id) {
      await productionStore.refreshMultiCompanyData([props.company]);
    }
  } catch (error) {
    globalErrorHandler(error);
  }
});

onUnmounted(() => {
  selection.clearSelection();
});
</script>

<style scoped lang="scss"></style>
